import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { Loader2, Mail, Send } from "lucide-react";

interface AddEmployeeDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: () => void;
}

type AppRole = "admin" | "hr_manager" | "hr_clerk" | "employee";

const AddEmployeeDialog = ({ open, onClose, onAdd }: AddEmployeeDialogProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    position: "",
    email: "",
  });
  const [createAccount, setCreateAccount] = useState(false);
  const [selectedRole, setSelectedRole] = useState<AppRole>("employee");
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast.error("Sie müssen angemeldet sein");
      return;
    }

    if (createAccount && !formData.email) {
      toast.error("Bitte geben Sie eine E-Mail-Adresse ein");
      return;
    }

    setSaving(true);
    
    const fullName = `${formData.firstName} ${formData.lastName}`.trim();
    
    try {
      // Create employee
      const { data: employeeData, error: employeeError } = await supabase
        .from('employees')
        .insert({
          name: fullName,
          position: formData.position,
          private_email: formData.email || null,
          created_by: user.id
        })
        .select()
        .single();

      if (employeeError) {
        console.error('Error adding employee:', employeeError);
        toast.error("Fehler beim Hinzufügen des Mitarbeiters");
        setSaving(false);
        return;
      }

      // If createAccount is checked, create user and send invitation
      if (createAccount && formData.email) {
        try {
          const { data, error } = await supabase.functions.invoke('invite-user', {
            body: {
              email: formData.email,
              fullName: fullName,
              employeeId: employeeData.id,
              role: selectedRole,
              createdBy: user.id
            }
          });

          if (error) {
            console.error('Error inviting user:', error);
            // Try to parse the error message from the function response
            let errorMessage = "Einladung konnte nicht gesendet werden";
            try {
              const errorData = JSON.parse(error.message || "{}");
              if (errorData.error) {
                errorMessage = errorData.error;
              }
            } catch {
              // Use default message
            }
            toast.warning(`Mitarbeiter erstellt, aber: ${errorMessage}`);
          } else if (data?.error) {
            // Handle error returned in data
            toast.warning(`Mitarbeiter erstellt, aber: ${data.error}`);
          } else if (data?.existingUser) {
            toast.info("Mitarbeiter erstellt und mit bestehendem Benutzer verknüpft");
          } else {
            toast.success(data?.message || "Mitarbeiter erstellt und Einladung gesendet");
          }
        } catch (inviteError: any) {
          console.error('Error inviting user:', inviteError);
          // Try to extract the actual error message
          let errorMessage = "Einladung konnte nicht gesendet werden";
          if (inviteError?.context?.body) {
            try {
              const body = JSON.parse(inviteError.context.body);
              if (body.error) {
                errorMessage = body.error;
              }
            } catch {
              // Use default message
            }
          }
          toast.warning(`Mitarbeiter erstellt, aber: ${errorMessage}`);
        }
      } else {
        toast.success("Mitarbeiter erfolgreich hinzugefügt");
      }

      setFormData({
        firstName: "",
        lastName: "",
        position: "",
        email: "",
      });
      setCreateAccount(false);
      setSelectedRole("employee");
      onAdd();
      onClose();
      
      // Navigate directly to the employee's detail page
      if (employeeData?.id) {
        navigate(`/employee/${employeeData.id}`);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error("Ein unerwarteter Fehler ist aufgetreten");
    } finally {
      setSaving(false);
    }
  };

  const getRoleLabel = (role: AppRole) => {
    switch (role) {
      case "admin": return "Administrator (voller Zugriff)";
      case "hr_manager": return "Personalmanager (Mitarbeiter verwalten)";
      case "hr_clerk": return "Personalverwalter (operative Daten)";
      case "employee": return "Mitarbeiter (nur eigene Daten)";
    }
  };

  const handleSendTestEmail = async () => {
    if (!formData.email.trim()) {
      toast.error("Bitte geben Sie zuerst eine E-Mail-Adresse ein");
      return;
    }

    setTestingEmail(true);

    try {
      const { error } = await supabase.functions.invoke("send-test-email", {
        body: { email: formData.email.trim() },
      });

      if (error) {
        toast.error(`Test-E-Mail fehlgeschlagen: ${error.message}`);
        return;
      }

      toast.success("Test-E-Mail wurde gesendet");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unbekannter Fehler";
      toast.error(`Test-E-Mail fehlgeschlagen: ${message}`);
    } finally {
      setTestingEmail(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Neuen Mitarbeiter hinzufügen</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">Vorname</Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
                placeholder="Max"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Nachname</Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
                placeholder="Mustermann"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="position">Position</Label>
            <Input
              id="position"
              value={formData.position}
              onChange={(e) => setFormData({ ...formData, position: e.target.value })}
              required
              placeholder="Bauarbeiter"
            />
          </div>
          
          <div className="border-t pt-4 space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="createAccount"
                checked={createAccount}
                onCheckedChange={(checked) => setCreateAccount(checked === true)}
              />
              <Label htmlFor="createAccount" className="text-sm font-medium cursor-pointer">
                Systemzugang erstellen und Einladung senden
              </Label>
            </div>
            
            {createAccount && (
              <div className="space-y-4 pl-6 animate-in fade-in slide-in-from-top-2">
                <div className="space-y-2">
                  <Label htmlFor="email">E-Mail-Adresse</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="mitarbeiter@firma.de"
                      className="pl-9"
                      required={createAccount}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Rolle</Label>
                  <Select value={selectedRole} onValueChange={(value: AppRole) => setSelectedRole(value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="employee">{getRoleLabel("employee")}</SelectItem>
                      <SelectItem value="hr_clerk">{getRoleLabel("hr_clerk")}</SelectItem>
                      <SelectItem value="hr_manager">{getRoleLabel("hr_manager")}</SelectItem>
                      <SelectItem value="admin">{getRoleLabel("admin")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleSendTestEmail}
                    disabled={saving || testingEmail || !formData.email.trim()}
                    className="w-full sm:w-fit"
                  >
                    {testingEmail ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Test-E-Mail wird gesendet...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        Test-E-Mail senden
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    Mit der Test-E-Mail prüfen Sie nur den Versandweg – ohne Benutzerkonto oder Einladung anzulegen.
                  </p>
                </div>
                <p className="text-xs text-muted-foreground">
                  Der Mitarbeiter erhält bei der echten Einladung eine E-Mail mit temporärem Passwort und muss dieses bei der ersten Anmeldung ändern.
                </p>
              </div>
            )}
          </div>
          
          <p className="text-sm text-muted-foreground">
            Nach dem Anlegen werden Sie direkt zu den Stammdaten weitergeleitet.
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Wird gespeichert...
                </>
              ) : (
                "Hinzufügen"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddEmployeeDialog;
